/*
 * Farida Gupta - "Read more" toggle for long announcement bar messages (sections/announcement-bar.liquid).
 *
 * Each message is clamped to one line in CSS (fg-theme.css). This script un-hides the toggle only for
 * messages whose text actually overflows that line, and re-checks on resize since overflow depends on
 * viewport width. While a message is expanded the carousel's autoplay is paused so it doesn't rotate
 * away mid-read; changing slides (swipe/autoplay) or scrolling the page collapses it again.
 */
const init = () => {
  const carousel = document.querySelector('.announcement-bar__carousel');

  if (!carousel || carousel.dataset.readMoreInit) {
    return;
  }

  carousel.dataset.readMoreInit = 'true';
  const messages = Array.from(carousel.querySelectorAll('.announcement-bar__message'));

  const getParts = (message) => ({
    text: message.querySelector('.announcement-bar__text'),
    toggle: message.querySelector('.announcement-bar__toggle'),
  });

  // Scroll position when a message was expanded; scrolling away from it collapses the message.
  let expandedAtScrollY = null;
  const SCROLL_COLLAPSE_THRESHOLD = 10;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let heightAnimation = null;

  // Expanding/collapsing resizes the sticky bar, which moves the header and page below it. Tween the
  // carousel's height (it has overflow: hidden in fg-theme.css) so that move is a slide, not a jump.
  const animateHeightChange = (change) => {
    const fromHeight = carousel.getBoundingClientRect().height;

    heightAnimation?.cancel();
    change();

    const toHeight = carousel.getBoundingClientRect().height;

    if (reduceMotion.matches || fromHeight === toHeight) {
      return;
    }

    heightAnimation = carousel.animate(
      [{ height: `${fromHeight}px` }, { height: `${toHeight}px` }],
      { duration: 300, easing: 'cubic-bezier(0.215, 0.61, 0.355, 1)' }
    );
  };

  const setExpanded = (message, expanded) => {
    const { toggle } = getParts(message);

    animateHeightChange(() => {
      message.classList.toggle('is-expanded', expanded);
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.textContent = expanded ? toggle.dataset.readLess : toggle.dataset.readMore;
    });

    if (expanded) {
      expandedAtScrollY = window.scrollY;
      carousel.player?.pause();
    } else if (!messages.some((item) => item.classList.contains('is-expanded'))) {
      expandedAtScrollY = null;
      carousel.player?.resume();
    }
  };

  const collapseAll = () => {
    messages.forEach((message) => {
      if (message.classList.contains('is-expanded')) {
        setExpanded(message, false);
      }
    });
  };

  // An expanded message is never clamped, so only measure collapsed ones.
  const updateToggleVisibility = () => {
    messages.forEach((message) => {
      if (message.classList.contains('is-expanded')) {
        return;
      }

      const { text, toggle } = getParts(message);
      toggle.hidden = text.scrollHeight <= text.clientHeight + 1;
    });
  };

  messages.forEach((message) => {
    const { toggle } = getParts(message);

    toggle.addEventListener('click', () => {
      setExpanded(message, !message.classList.contains('is-expanded'));
    });
  });

  carousel.addEventListener('carousel:change', collapseAll);

  window.addEventListener(
    'scroll',
    () => {
      if (expandedAtScrollY !== null && Math.abs(window.scrollY - expandedAtScrollY) > SCROLL_COLLAPSE_THRESHOLD) {
        collapseAll();
      }
    },
    { passive: true }
  );

  new ResizeObserver(updateToggleVisibility).observe(carousel);
  document.fonts?.ready.then(updateToggleVisibility);
  updateToggleVisibility();
};

init();

// Theme editor re-renders the section without re-running this module.
document.addEventListener('shopify:section:load', init);
